import React from 'react';
import { motion } from 'framer-motion';

const ISSUE_TAGS = [
  { key: 'low_energy', label: 'Low energy' },
  { key: 'poor_sleep', label: 'Poor sleep' },
  { key: 'high_stress', label: 'High stress' },
  { key: 'pain', label: 'Pain' },
  { key: 'anxiety', label: 'Anxiety' },
  { key: 'low_mood', label: 'Low mood' },
  { key: 'brain_fog', label: 'Brain fog' },
  { key: 'appetite_changes', label: 'Appetite changes' },
  { key: 'loneliness', label: 'Loneliness' },
  { key: 'overwhelmed', label: 'Overwhelmed' },
];

const STATE_SIGNALS = [
  { key: 'stable', label: 'Nothing major right now', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { key: 'worsening', label: 'Things are getting worse', color: 'bg-red-50 text-red-600 border-red-200' },
  { key: 'improving', label: 'Things are improving', color: 'bg-blue-50 text-blue-600 border-blue-200' },
];

export const OnboardingCurrentState = ({ data, onChange }) => {
  const state = data.current_state || { feeling_description: '', current_issues: [], hardest_right_now: '', overall_signal: '' };

  const update = (field, value) => {
    onChange({ ...data, current_state: { ...state, [field]: value } });
  };

  const toggleIssue = (key) => {
    const issues = state.current_issues || [];
    const updated = issues.includes(key) ? issues.filter(i => i !== key) : [...issues, key];
    update('current_issues', updated);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="bg-white border border-slate-150 rounded-2xl p-5">
        <p className="text-slate-700 leading-relaxed">
          How are you feeling at the moment?
        </p>
        <p className="text-sm text-slate-400 mt-2">
          Tell me what feels most noticeable right now — even a few words helps.
        </p>
      </div>

      {/* Overall signal */}
      <div>
        <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 block">Overall</label>
        <div className="flex flex-col gap-2 sm:flex-row">
          {STATE_SIGNALS.map(({ key, label, color }) => (
            <button key={key} onClick={() => update('overall_signal', state.overall_signal === key ? '' : key)}
              className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium border transition-colors ${
                state.overall_signal === key ? color : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
              }`} data-testid={`state-signal-${key}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Feeling description */}
      <div>
        <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 block">How you're feeling</label>
        <textarea
          data-testid="onboarding-feeling"
          placeholder="A few words about where you're at right now..."
          value={state.feeling_description}
          onChange={(e) => update('feeling_description', e.target.value)}
          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20 resize-none h-24"
        />
      </div>

      {/* Issue tags */}
      <div>
        <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3 block">Current issues</label>
        <div className="flex flex-wrap gap-2">
          {ISSUE_TAGS.map(({ key, label }) => {
            const selected = (state.current_issues || []).includes(key);
            return (
              <button key={key} data-testid={`onboarding-issue-${key}`} onClick={() => toggleIssue(key)}
                className={`px-3.5 py-2 rounded-full text-sm transition-all border ${
                  selected ? 'bg-[#235347] text-white border-[#235347]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Hardest right now */}
      <div>
        <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 block">What's hardest right now?</label>
        <input data-testid="onboarding-hardest" placeholder="Optional — but even a few words helps"
          value={state.hardest_right_now} onChange={(e) => update('hardest_right_now', e.target.value)}
          className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20" />
      </div>
    </motion.div>
  );
};
